/**
 * Bug report data model, storage, and helpers.
 *
 * Storage layout (May 2026 — Fabian asked why the bug DB took 2s to load):
 *
 *   bug_reports:index           array of BugReportListItem (no screenshotUrl)
 *   bug_reports:row:<id>        full BugReport for one bug (with screenshotUrl)
 *   bug_reports:all             LEGACY single-blob layout — kept as rollback
 *                                backup, NEVER WRITTEN to going forward, only
 *                                read once during the first migration.
 *
 * Before the split, every list GET read the full 4.4 MB blob (98% of which
 * was base64 screenshots) just to render a row table. TTFB was 1.5 s. The
 * split means:
 *   - List endpoint reads only the small index (≤100 KB even at 100s of
 *     bugs) — TTFB drops to network-bound (~250 ms).
 *   - Detail endpoint reads only the single row needed (typical 50 KB,
 *     biggest 590 KB) — also much faster.
 *   - PATCH/POST/DELETE no longer rewrite 4.4 MB on every mutation; they
 *     update one row + the small index.
 *
 * Migration: lazy. The first call into `ensureMigrated()` after deploy
 * splits the legacy blob into per-row keys + the index. Idempotent —
 * subsequent calls are a single existence check on the index. The legacy
 * key is left intact so a rollback can read it again.
 */

import { kvGet, kvSet, kvDel, kvMutate } from '@/lib/kv'

// ── KV keys ──────────────────────────────────────────────────────────────────

/** Legacy single-blob key — read once during migration, never written. */
const LEGACY_KEY = 'bug_reports:all'
/** New: compact list-shape index (BugReportListItem[]). */
const INDEX_KEY = 'bug_reports:index'
/** New: per-bug full-row prefix. Full key = ROW_PREFIX + bug.id */
const ROW_PREFIX = 'bug_reports:row:'

function rowKey(id: string): string {
  return ROW_PREFIX + id
}

// ── Enums / constants ────────────────────────────────────────────────────────

export const BUG_APPROVAL_STATUSES = ['pending', 'approved', 'rejected'] as const
export type BugApprovalStatus = typeof BUG_APPROVAL_STATUSES[number]

export const BUG_CATEGORIES = [
  'bug', 'data-issue', 'ux-issue', 'performance', 'feature-request', 'other',
] as const
export type BugCategory = typeof BUG_CATEGORIES[number]

export const BUG_SEVERITIES = ['blocker', 'high', 'medium', 'low'] as const
export type BugSeverity = typeof BUG_SEVERITIES[number]

export const BUG_STATUSES = [
  'new', 'triaged', 'in_progress', 'blocked', 'fixed', 'verified', 'closed',
] as const
export type BugStatus = typeof BUG_STATUSES[number]

export const BUG_PRIORITIES = ['critical', 'high', 'medium', 'low'] as const
export type BugPriority = typeof BUG_PRIORITIES[number]

export const BUG_FREQUENCIES = ['always', 'often', 'sometimes', 'once'] as const
export type BugFrequency = typeof BUG_FREQUENCIES[number]

export const BUG_ENVIRONMENTS = ['production', 'staging', 'local'] as const
export type BugEnvironment = typeof BUG_ENVIRONMENTS[number]

// ── Projects ─────────────────────────────────────────────────────────────────

/**
 * WHICH APP a bug was filed against.
 *
 * One bug database, several products (operator request, Aug 2026). The Goodiez
 * Portal and the AndreiUber driver app (`/andrei`) are different codebases'
 * worth of surface area sharing one KV store, one triage roster and one
 * resolve/notify flow — splitting the storage would have duplicated all of
 * that. A tag is enough to keep the two queues apart.
 *
 * `portal` is the DEFAULT and the backfill value: every bug filed before this
 * field existed came from the portal, so an absent `project` reads as
 * `'portal'` rather than as an error or an "untagged" fourth state. That is
 * what makes the migration a no-op — see `normalizeBugProject`.
 *
 * The vocabulary is a `const` array, not a bare union, because the filter
 * buttons, the chip colours and the API validator must all iterate the SAME
 * list — the July 2026 roster white-screen came from a colour map and an API
 * validator disagreeing about a vocabulary.
 */
export const BUG_PROJECTS = ['portal', 'andrei-uber', 'website'] as const
export type BugProject = typeof BUG_PROJECTS[number]

/** Where an untagged (pre-`project`) row belongs. */
export const DEFAULT_BUG_PROJECT: BugProject = 'portal'

/**
 * Display name per project. ONE definition — the roster buttons, the roster
 * chip, the detail page and both email templates all read this map. A second
 * hardcoded "AndreiUber" string somewhere is exactly the class of drift the
 * repo's grep-before-you-code rule exists to prevent.
 */
export const BUG_PROJECT_LABELS: Record<BugProject, string> = {
  'portal': 'Portal',
  'andrei-uber': 'AndreiUber',
  // The public Swifts brand site (repo fabiant314/green-labs-product-concepts, served on
  // goodiezgummies.com). Added Sep 5 2026 on the operator's directive so website reports are
  // filed, triaged and worked like portal ones.
  'website': 'Swifts Website',
}

/** Emoji per project, used on the roster filter buttons and chips. */
export const BUG_PROJECT_ICONS: Record<BugProject, string> = {
  'portal': '\u{1F3ED}',      // 🏭
  'andrei-uber': '\u{1F697}', // 🚗
  'website': '\u{1F310}',     // 🌐
}

/** Chip colours per project. Same shape as the severity/status maps. */
export const BUG_PROJECT_COLORS: Record<BugProject, { bg: string; text: string }> = {
  'portal': { bg: '#2563eb', text: '#fff' },
  'andrei-uber': { bg: '#0f766e', text: '#fff' },
  'website': { bg: '#7c3aed', text: '#fff' },
}

/**
 * Coerce ANY value to a canonical project. Unknown/absent → `portal`.
 *
 * This is the whole migration. Rows written before the field existed have no
 * `project` key; rather than rewrite ~120 KV rows (and risk a partial pass),
 * every read path funnels through here, so a legacy row simply reads as
 * Portal. Idempotent, pure, and safe to call on data from anywhere.
 */
export function normalizeBugProject(value: unknown): BugProject {
  return (BUG_PROJECTS as readonly string[]).includes(value as string)
    ? value as BugProject
    : DEFAULT_BUG_PROJECT
}

/** Human label for a (possibly absent/unknown) project value. */
export function bugProjectLabel(value: unknown): string {
  return BUG_PROJECT_LABELS[normalizeBugProject(value)]
}

/** The portal's EMAIL name. Longer than the roster chip's "Portal" because it appears in an
 *  inbox, where "Portal" alone says nothing about whose portal. */
export const BUG_TRACKER_PORTAL_NAME = 'Goodies Portal'

/**
 * Email-facing name of the app a bug was filed against — the ONE definition, shared by the
 * filing notification, the resolution email and every bug-pipeline email.
 *
 * It lived as a private `trackerName` copy in two routes while a THIRD place (the pipeline cron)
 * hardcoded the portal name on the grounds that the pipeline only ever worked portal bugs. That
 * stopped being true the moment the website project joined PIPELINE_PROJECTS (Sep 5 2026), and a
 * hardcoded copy is exactly how a Swifts Website recommendation would have arrived branded
 * "Goodies Portal".
 */
export function bugTrackerName(report: Pick<BugReport, 'project'>): string {
  return normalizeBugProject(report.project) === 'portal'
    ? BUG_TRACKER_PORTAL_NAME
    : bugProjectLabel(report.project)
}

/**
 * Stamp a canonical `project` onto a stored row. The read-side counterpart of
 * `normalizeBugProject` — apply it at every KV read so no surface downstream
 * has to remember that the field may be missing.
 */
export function withBugProject<T extends { project?: unknown }>(row: T): T & { project: BugProject } {
  return { ...row, project: normalizeBugProject(row.project) }
}

/** Known Goodiez Portal areas for the "affected area" picker. */
export const KNOWN_AREAS = [
  'Dashboard',
  'Recipes / Recipe Books',
  'Inventory',
  'Production / Scheduler',
  'Planning Lab',
  'Maintenance / Equipment',
  'Sales',
  'Employees',
  'Settings',
  'Other',
] as const

/**
 * Known AndreiUber areas — the driver app's six tabs plus the cross-cutting
 * surfaces a driver would actually name. Portal areas ("Planning Lab",
 * "Recipes") are meaningless on that app, so the picker must switch with the
 * project instead of offering one merged list.
 */
export const ANDREI_AREAS = [
  'Now (current pick)',
  'Plan (week ahead)',
  'Map',
  'Tips',
  'Off-hours',
  'Intel',
  'Earnings / rate numbers',
  'Access / opening the link',
  'Email alerts',
  'Other',
] as const

/**
 * Swifts website areas — the public site's own sections, in the LOCKED taxonomy the page uses
 * (4 collections, formats beneath them, the lineup, the locator, the trail). A reporter names
 * where they were on the site; portal areas like "Planning Lab" are meaningless there.
 */
export const WEBSITE_AREAS = [
  'Homepage / hero',
  'Collections (Gummies, Chocolates, Candies, Other)',
  'The Lineup (product grid + filters)',
  'Product view (photos, dose, details)',
  'Find Your Swifts (store locator)',
  'Walkable trail (3D)',
  'Story / editorial sections',
  'Navigation / footer',
  'Phone layout',
  'Other',
] as const

/** Area picker options per project. */
export const BUG_PROJECT_AREAS: Record<BugProject, readonly string[]> = {
  'portal': KNOWN_AREAS,
  'andrei-uber': ANDREI_AREAS,
  'website': WEBSITE_AREAS,
}

/** Area options for a (possibly absent/unknown) project value. */
export function areasForProject(project: unknown): readonly string[] {
  return BUG_PROJECT_AREAS[normalizeBugProject(project)]
}

/**
 * Placeholder EXAMPLES for the report form, per project.
 *
 * These live here with the areas, and for the same reason: the form's example
 * copy is project-specific content, and leaving the portal's examples on the
 * driver app shipped a form that told Andrei to "Go to Planning Lab" and
 * "Run Quarterly Simulation" — screens that do not exist in his app. Caught on
 * the live page during verification; the area list had been switched but the
 * example strings had not, which is precisely the partial-coverage failure the
 * repo's grep-before-you-code rule exists to prevent.
 *
 * Any NEW project-specific form copy belongs in this map, never inline in the
 * component — inline is how the next one gets missed.
 */
export const BUG_PROJECT_EXAMPLES: Record<BugProject, { title: string; steps: string }> = {
  'portal': {
    title: 'e.g. "Planning Lab shows wrong batch size for Honey"',
    steps: '1. Go to Planning Lab\n2. Click "Run Quarterly Simulation"\n3. Look at Honey row\n4. See "NOT FEASIBLE" error',
  },
  'andrei-uber': {
    title: 'e.g. "Plan tab shows Saturday\'s earnings twice"',
    steps: '1. Open the Plan tab\n2. Scroll to Saturday\n3. Look at the $/hr line\n4. The same number is listed twice',
  },
  'website': {
    title: 'e.g. "Find Your Swifts returns no stores for Boysenberry Jam"',
    steps: '1. Open the Swifts homepage\n2. Scroll to Find Your Swifts\n3. Pick Boysenberry Jam\n4. Enter 98101 — the list comes back empty',
  },
}

/** Form example copy for a (possibly absent/unknown) project value. */
export function examplesForProject(project: unknown): { title: string; steps: string } {
  return BUG_PROJECT_EXAMPLES[normalizeBugProject(project)]
}

/**
 * One line under the form's app picker saying what each choice MEANS.
 *
 * Lives here with the areas and the examples, for the same reason: a reporter signed into the
 * portal choosing "Swifts Website" is filing against a codebase they cannot see, and the sentence
 * that tells them so is project-specific copy. Inline in the component it would be the exact
 * partial-coverage failure the areas/examples maps already exist to prevent.
 */
export const BUG_PROJECT_HINTS: Record<BugProject, string> = {
  'portal': 'This portal — the internal app you are signed into.',
  'andrei-uber': 'The AndreiUber driver app.',
  'website': 'The public Swifts site (swiftsedibles.com) — its fixes ship from the website repo.',
}

/** Picker hint for a (possibly absent/unknown) project value. */
export function hintForProject(project: unknown): string {
  return BUG_PROJECT_HINTS[normalizeBugProject(project)]
}

// ── Schema ───────────────────────────────────────────────────────────────────

export interface BugReport {
  id: string
  createdAt: string            // ISO 8601
  updatedAt: string            // ISO 8601

  /**
   * Which app this bug is against. Rows written before Aug 2026 lack the field
   * entirely; every read path normalizes it to `portal` (see
   * `normalizeBugProject`), so treat it as always-present downstream.
   */
  project: BugProject

  // Reporter-provided fields
  title: string
  description: string
  expectedBehavior: string
  stepsToReproduce: string
  category: BugCategory
  severity: BugSeverity
  affectedArea: string         // one of KNOWN_AREAS or custom text
  environment: BugEnvironment
  frequency: BugFrequency
  blockedWork: boolean
  workaroundFound: boolean
  workaroundDetails: string

  // Auto-captured context
  currentUrl: string
  currentRoute: string
  reporterName: string
  reporterEmail: string
  browserInfo: string
  osInfo: string
  deviceInfo: string
  viewport: string
  appVersion: string
  reproductionContext: string   // JSON string with additional auto-captured metadata

  // Workflow / triage
  status: BugStatus
  priority: BugPriority
  assignee: string | null
  resolutionSummary: string | null
  closedAt: string | null       // ISO 8601

  // ── Attachments ────────────────────────────────────────────────────────
  // Screenshots are base64 data URIs stored inline on the row.
  //
  // The storage split is deliberate (BUG-mskg1hlz-71vl, Aug 2026 — Jordan
  // asked to attach two shots "for compare/contrast"): `screenshotUrl` keeps
  // its ORIGINAL meaning as the first/primary image, and the extras live in a
  // separate array. Two reasons it is not one flat array:
  //
  //   1. Every pre-existing reader of `screenshotUrl` keeps working untouched,
  //      so a surface nobody remembered to update still shows the first image
  //      instead of silently showing none.
  //   2. No duplication. Mirroring image #1 into both fields would add ~100 KB
  //      per row to a KV value that already carries the base64 payloads.
  //
  // NEVER read these two fields directly to display attachments — call
  // `bugScreenshots(row)`, which is the one place that knows the ordering and
  // the legacy shape.
  /** First/primary screenshot. Legacy field name — predates multi-attach. */
  screenshotUrl: string | null
  /** Screenshots 2..N, in the order the reporter added them. */
  additionalScreenshotUrls: string[]
  /**
   * NON-image files (spreadsheet, PDF, export), in the order the reporter added
   * them. Separate from the screenshot fields on purpose — see the
   * "Non-image attachments" section below. Read via `bugAttachments(row)`.
   */
  attachments: BugAttachment[]

  // Approval workflow
  approvalStatus: BugApprovalStatus
  approvedForFixAt: string | null   // ISO 8601
  approvedBy: string | null
  approvalSource: 'email_link' | 'ui' | 'auto_blocker' | null
  approvalNote: string | null

  // ── Fix pipeline (docs/features/bug-fix-pipeline) ──────────────────────
  // Optional on purpose: rows predate the feature, and every consumer treats
  // absence as "the pipeline has not touched this bug". The walked field
  // list, the stage machine, and the patch builders live in
  // lib/bug-pipeline.ts (PIPELINE_UPDATE_FIELDS).
  /** The tick's fix recommendation — plain text, sent to the operator verbatim. */
  fixRecommendation?: string | null
  /**
   * The repo-relative files the recommendation intends to change — the BLAST RADIUS, declared
   * before approval so the email can show it. See `lib/bug-pipeline-protected.ts`: a plan touching
   * a shared surface (the welcome grid, the DI shell, the ACL, the cron schedule) cannot be
   * authorised by a queue-level "Approve all" or by the blocker self-approval; it needs a tap that
   * names the file.
   */
  pipelinePlannedFiles?: string[] | null
  fixRecommendationConfidence?: 'high' | 'medium' | 'low' | null
  /** The tick's verdict on the report — 'fix' is an outcome, never a default
   *  (RECOMMENDATION_VERDICTS in lib/bug-pipeline.ts). Absent on rows
   *  recommended before Sep 4 2026. */
  fixRecommendationVerdict?: 'fix' | 'counter-proposal' | 'wont-fix' | 'needs-decision' | null
  fixRecommendationAt?: string | null      // ISO 8601 — when the tick wrote the recommendation
  recommendationEmailedAt?: string | null  // stamped by the notify cron after the operator email sends
  pipelineClaimedAt?: string | null        // when the tick claimed the fix (stale-claim detection)
  pipelineAttempts?: number                // fix attempts started; MAX_PIPELINE_ATTEMPTS caps retries
  pipelineFixCommit?: string | null        // commit sha the tick pushed for this bug
  pipelineFixPushedAt?: string | null      // when it pushed — the verify stage keys off this
  /**
   * The fix's TECHNICAL notes — the commit message(s) that fixed this bug: what changed, why, how it
   * was verified, what was left for a person. `resolutionSummary` is the six plain lines the REPORTER
   * is emailed; this is the engineer's account. Until Oct 2026 it lived only in git and the run log
   * (BUG_WORKFLOW.md: "the technical detail belongs in the git commit message"), so the bug page
   * showed a resolution and nothing about what was actually done (operator, Oct 3 2026: "You should
   * absolutely update the bug with the comments you have when resolving it"). Built by
   * `lib/bug-fix-notes.ts`; written at `fix-pushed`, and backfilled for every earlier fix.
   * ROW-ONLY: `toBugListItem` blanks it — the index is read on every list, board and tick.
   */
  pipelineFixNotes?: string | null
  resolveEmailPending?: boolean            // tick requested resolution; the notify cron finishes it via /resolve
  resolveEmailedAt?: string | null         // stamped after the reporter resolution email sent
  pipelineNotice?: string | null           // operator-facing pipeline note (blockers, verify failures)
  pipelineNoticeEmailedAt?: string | null  // stamped after the notice email sent
  /** TRUE ⇔ this notice is a question the REPORTER should see (every block, unless the tick
   *  marked it operator-only). The notice email then leads to them, CC'ing the operator. */
  pipelineNoticeAsksReporter?: boolean
  /** Stamped when the pipeline emailed the REPORTER + operator asking them to answer the
   *  plan's question about this member. Its own stamp (not the plan's `emailedAt`) so a plan
   *  already sent still chases an unanswered question, and so it is asked exactly once. */
  pipelineQuestionEmailedAt?: string | null
  pipelineBlockedReason?: string | null    // set → the pipeline leaves this bug alone until unblocked
  pipelineOperatorGuidance?: string | null   // the operator's answer/instruction for the fixing agent
  pipelineOperatorGuidanceAt?: string | null // when it was written — the tick treats newest as current
  /** Feature work items only: the slice log, one entry per shipped slice,
   *  each ending in "NEXT: …" — the cursor the next run continues from. */
  pipelineWorkLog?: string | null
  /** Intake queue this bug was placed in on arrival (lib/bug-queues.ts). Absent/null =
   *  filed before intake queues existed, or excluded by design (non-portal, blocker). */
  pipelineQueueId?: string | null
  /**
   * Feature-work consolidation (item C): the `[CLAUDE feature work]` item this report was folded
   * into while the operator decides on it. Stamped when that item is FILED — the report stays
   * `new`, it is NOT closed yet. Approving the item closes it as Duplicate Work; declining the
   * consolidation CLEARS this and the report returns to individual handling. While the stamp
   * stands the report is spoken for: the pipeline neither recommends nor mails about it on its
   * own (consolidationHold in lib/bug-pipeline.ts).
   */
  consolidatedInto?: string | null
  /**
   * How a CLOSED report ended, when it was not the ordinary fixed path. `'duplicate'` ⇔ closed as
   * Duplicate Work of `duplicateOf`. Absent on every row closed any other way (won't-fix, manual
   * close) and on every row closed before item C — absence means "the ordinary path", never
   * "unknown kind".
   */
  resolutionKind?: 'duplicate' | null
  /** The tracker item this report duplicates — written together with `resolutionKind: 'duplicate'`. */
  duplicateOf?: string | null
}

// ── Screenshots ──────────────────────────────────────────────────────────────

/**
 * How many screenshots one bug may carry.
 *
 * Screenshots are base64 data URIs stored inline on the KV row, so the cap is
 * a storage guard, not a UI preference: a full-resolution PNG averages ~100 KB
 * and the reporter-side file limit is 4 MB each. Four keeps the worst-case row
 * bounded while comfortably covering the reported need (a before/after pair).
 */
export const MAX_BUG_SCREENSHOTS = 4

/** Anything holding the two screenshot fields — a full row or a bare patch. */
type ScreenshotBearing = {
  screenshotUrl?: string | null
  additionalScreenshotUrls?: string[] | null
}

/**
 * THE canonical way to read a bug's screenshots. Returns every attachment in
 * reporter order, primary first, with empties dropped.
 *
 * Every display surface must go through this. Reading `screenshotUrl` directly
 * silently shows only the first of N; reading `additionalScreenshotUrls`
 * directly silently drops the first. Both are invisible failures — the page
 * renders fine, just without the evidence the reporter attached.
 *
 * Tolerates rows written before `additionalScreenshotUrls` existed (the field
 * is simply absent) and defends against a non-array landing there.
 */
export function bugScreenshots(row: ScreenshotBearing | null | undefined): string[] {
  if (!row) return []
  const out: string[] = []
  if (typeof row.screenshotUrl === 'string' && row.screenshotUrl.length > 0) {
    out.push(row.screenshotUrl)
  }
  if (Array.isArray(row.additionalScreenshotUrls)) {
    for (const u of row.additionalScreenshotUrls) {
      if (typeof u === 'string' && u.length > 0) out.push(u)
    }
  }
  return out
}

/**
 * Split a flat, client-supplied screenshot list into the stored shape.
 *
 * The wire format is one ordered array (`screenshotUrls`) because that is what
 * a form naturally produces; the primary/extras split is a storage detail that
 * stops here. Drops empties, de-duplicates (a double-paste of the same image
 * would otherwise cost another ~100 KB for nothing), and truncates to
 * `MAX_BUG_SCREENSHOTS` so an over-long list can never reach KV — validation
 * rejects that case first, but this stays safe if it is ever called directly.
 */
export function splitScreenshots(
  urls: unknown,
): { screenshotUrl: string | null; additionalScreenshotUrls: string[] } {
  const list = Array.isArray(urls) ? urls : []
  const clean: string[] = []
  for (const u of list) {
    if (typeof u !== 'string' || u.length === 0) continue
    if (clean.includes(u)) continue
    clean.push(u)
    if (clean.length >= MAX_BUG_SCREENSHOTS) break
  }
  return {
    screenshotUrl: clean[0] ?? null,
    additionalScreenshotUrls: clean.slice(1),
  }
}

/**
 * Normalize whatever an API caller sent into the stored shape.
 *
 * Accepts the new `screenshotUrls: string[]` wire field, the legacy single
 * `screenshotUrl: string`, or both (the array wins — a client sending both is
 * a new client filling the legacy field for safety). Returns the exact pair of
 * fields to persist, so create and update paths cannot disagree.
 */
export function normalizeScreenshotInput(
  body: { screenshotUrls?: unknown; screenshotUrl?: unknown },
): { screenshotUrl: string | null; additionalScreenshotUrls: string[] } {
  if (Array.isArray(body.screenshotUrls)) return splitScreenshots(body.screenshotUrls)
  if (typeof body.screenshotUrl === 'string' && body.screenshotUrl.length > 0) {
    return splitScreenshots([body.screenshotUrl])
  }
  return { screenshotUrl: null, additionalScreenshotUrls: [] }
}

// ── Non-image attachments ────────────────────────────────────────────────────
//
// BUG (Jordan, Aug 2026): he could not attach an Excel file. The picker was
// hard-filtered to `image/*` in THREE places — the input's `accept`, the upload
// handler and the paste handler — so a spreadsheet was dropped on the floor with
// no message at all. Picking the file appeared to do nothing.
//
// Stored as a PARALLEL field rather than folded into the screenshot arrays, for
// the same reason `screenshotUrl` was kept when the extras array arrived:
//
//   1. Every existing `bugScreenshots()` reader keeps working untouched. A
//      surface that renders shots in an <img> must never be handed a .xlsx.
//   2. A screenshot needs no filename; an attachment is USELESS without one
//      ("download" with no name tells the reader nothing). Different shape,
//      different field.

/** A non-image file attached to a bug — a spreadsheet, a PDF, an export. */
export interface BugAttachment {
  /** Original filename, as the reporter's disk had it. Shown, and used for download. */
  name: string
  /** MIME type as the browser reported it (may be '' for unknown types). */
  type: string
  /** base64 data URI — same inline storage model as screenshots. */
  dataUrl: string
}

/**
 * How many non-image files one bug may carry (on top of its screenshots).
 * Operator, Aug 2026: "up to 10 files".
 *
 * Note how this interacts with the byte ceilings below — the COUNT is rarely
 * what stops a reporter. Ten files are allowed, but they share one ~4 MB request,
 * so ten 500 KB spreadsheets will hit `MAX_BUG_UPLOAD_BYTES` first. That is a
 * platform bound, not a preference, and the error says which limit was hit.
 */
export const MAX_BUG_ATTACHMENTS = 10

/**
 * Per-file and whole-submission ceilings.
 *
 * These are NOT arbitrary. The create POST carries every screenshot AND every
 * attachment inline as base64 in one JSON body, and a Vercel serverless request
 * body is capped around 4.5 MB — past that the request fails at the platform,
 * before any of our code runs, which the reporter sees as a generic failure. The
 * total guard is what makes the failure legible; the per-file guard is what makes
 * it actionable ("this file is too big" beats "your report didn't send").
 *
 * BUG-mtkx9th0-g27g (Holly, Sept 2026): "network error — please try again" filing
 * a bug from a phone. The ceiling was 4 MB measured on the DECODED bytes — but
 * the wire carries BASE64, which inflates by 4/3, so a 4 MB submission is 5.33 MB
 * on the wire. Everything between ~3.3 MB and 4 MB passed our validation and then
 * died at the platform with a non-JSON 413, i.e. exactly the illegible failure
 * these constants exist to prevent. Phone screenshots are several MB each, so the
 * gap was ordinary, not exotic.
 *
 * The ceiling is therefore derived, not chosen: the decoded budget is whatever
 * survives base64 inflation inside the platform's body limit, less headroom for
 * the form's text fields and the JSON overhead.
 */
/** What the platform will carry. Not ours to change — Vercel's serverless body cap. */
export const PLATFORM_REQUEST_BYTES = Math.floor(4.5 * 1024 * 1024)
/** Room for the prose fields, the reproduction context and the JSON scaffolding. */
const UPLOAD_OVERHEAD_BYTES = 256 * 1024
/** Decoded bytes that fit once base64 has inflated them by 4/3. ≈ 3.0 MB. */
export const MAX_BUG_UPLOAD_BYTES = Math.floor((PLATFORM_REQUEST_BYTES - UPLOAD_OVERHEAD_BYTES) * 3 / 4)
/** One file may fill the whole budget, but never more than the report can carry. */
export const MAX_ATTACHMENT_BYTES = Math.min(3 * 1024 * 1024, MAX_BUG_UPLOAD_BYTES)

/**
 * Decoded bytes a submission would put on the wire — screenshots AND attachments,
 * which share one request body.
 *
 * THE one measurement, shared by the client's pre-flight and the server's
 * validation. They were separately written before, which is how the client came
 * to have no total check at all: the form happily encoded 4 phone screenshots and
 * posted a request the platform rejected before any of our code could explain why.
 */
export function bugUploadBytes(data: {
  screenshotUrls?: unknown
  attachments?: unknown
}): number {
  let total = 0
  const shots = Array.isArray(data.screenshotUrls) ? data.screenshotUrls : []
  for (const u of shots) if (typeof u === 'string') total += dataUrlBytes(u)
  const files = Array.isArray(data.attachments) ? data.attachments : []
  for (const raw of files) {
    if (!raw || typeof raw !== 'object') continue
    const dataUrl = (raw as Record<string, unknown>).dataUrl
    if (typeof dataUrl === 'string') total += dataUrlBytes(dataUrl)
  }
  return total
}

/**
 * The ONE phrasing of the too-big refusal, so the pre-flight in the form and the
 * server's rejection read identically — a reporter who hits it twice must not be
 * told two different stories about the same limit.
 */
export function bugUploadTooLargeMessage(totalBytes: number): string {
  return `Attachments total ${mbLabel(totalBytes)} — the limit is ${mbLabel(MAX_BUG_UPLOAD_BYTES)} per report. Remove one and file a follow-up.`
}

/**
 * The file types a reporter may attach. An ALLOW-list, deliberately: a bug
 * tracker is not a file share, and anything not on this list is something we
 * have no reason to accept from a browser.
 *
 * Matched on EXTENSION, not MIME. Browsers disagree about spreadsheet MIME types
 * — Windows reports `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
 * for a .xlsx, some setups report `application/octet-stream`, and a .csv arrives
 * as `text/csv`, `application/vnd.ms-excel` or `text/plain` depending on what
 * else is installed. Trusting MIME here is exactly how "I attached it and nothing
 * happened" happens.
 */
export const BUG_ATTACHMENT_EXTENSIONS: readonly string[] = [
  '.xlsx', '.xlsm', '.xls', '.csv',
  '.pdf',
  '.docx', '.doc',
  '.txt', '.json',
]

/** The `accept` attribute for the file picker — images plus every allowed doc type. */
export const BUG_UPLOAD_ACCEPT = ['image/*', ...BUG_ATTACHMENT_EXTENSIONS].join(',')

/** Lowercased extension of a filename, including the dot ('' when there is none). */
export function fileExtension(name: string): string {
  const i = String(name ?? '').lastIndexOf('.')
  return i < 0 ? '' : String(name).slice(i).toLowerCase()
}

/** Is this a file type a reporter may attach? Extension-based — see the list above. */
export function isAllowedAttachmentName(name: string): boolean {
  return BUG_ATTACHMENT_EXTENSIONS.includes(fileExtension(name))
}

/**
 * Decoded byte size of a base64 data URI. Used by BOTH the client (to reject a
 * file before it is ever encoded into a request) and the server (which cannot
 * trust the client to have done so).
 */
export function dataUrlBytes(dataUrl: string): number {
  const s = String(dataUrl ?? '')
  const comma = s.indexOf(',')
  if (comma < 0) return 0
  const payload = s.slice(comma + 1)
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding)
}

/** Anything holding the attachment field — a full row or a bare patch. */
type AttachmentBearing = { attachments?: BugAttachment[] | null }

/**
 * THE canonical way to read a bug's non-image attachments. Tolerates rows
 * written before the field existed (simply absent) and drops malformed entries
 * rather than handing a render surface an object with no `dataUrl`.
 */
export function bugAttachments(row: AttachmentBearing | null | undefined): BugAttachment[] {
  if (!row || !Array.isArray(row.attachments)) return []
  const out: BugAttachment[] = []
  for (const a of row.attachments) {
    if (!a || typeof a !== 'object') continue
    const dataUrl = typeof a.dataUrl === 'string' ? a.dataUrl : ''
    if (!dataUrl) continue
    out.push({
      name: typeof a.name === 'string' && a.name.length > 0 ? a.name : 'attachment',
      type: typeof a.type === 'string' ? a.type : '',
      dataUrl,
    })
  }
  return out
}

/**
 * Normalize whatever an API caller sent into the stored attachment list. Drops
 * junk, de-duplicates by payload, and truncates to `MAX_BUG_ATTACHMENTS` so an
 * over-long list can never reach KV — validation rejects that case first, but
 * this stays safe if it is ever called directly.
 */
export function normalizeAttachmentInput(body: { attachments?: unknown }): BugAttachment[] {
  if (!Array.isArray(body.attachments)) return []
  const clean: BugAttachment[] = []
  for (const a of body.attachments) {
    if (!a || typeof a !== 'object') continue
    const rec = a as Record<string, unknown>
    const dataUrl = typeof rec.dataUrl === 'string' ? rec.dataUrl : ''
    const name = typeof rec.name === 'string' ? rec.name : ''
    if (!dataUrl || !name) continue
    if (clean.some(c => c.dataUrl === dataUrl)) continue
    clean.push({ name, type: typeof rec.type === 'string' ? rec.type : '', dataUrl })
    if (clean.length >= MAX_BUG_ATTACHMENTS) break
  }
  return clean
}

// ── Priority computation ─────────────────────────────────────────────────────

/**
 * Compute initial priority from severity + blocking status.
 *
 * Rule:
 *   blocker severity OR blockedWork === true → critical
 *   high severity                           → high
 *   medium severity                         → medium
 *   low severity                            → low
 */
export function computePriority(severity: BugSeverity, blockedWork: boolean): BugPriority {
  if (severity === 'blocker' || blockedWork) return 'critical'
  if (severity === 'high') return 'high'
  if (severity === 'medium') return 'medium'
  return 'low'
}

/**
 * Re-derive priority when a TRIAGE INPUT changes — the follow-through
 * `computePriority` never had.
 *
 * BUG-mucb7ttp-rujy (Fabian, Sep 22 2026): "ensure that when a bug is promoted
 * to blocking from a different severity, the pipeline reorders priority queue
 * accordingly." It did not. Priority was derived from severity EXACTLY ONCE, at
 * filing (`createBugReport`). Editing severity to `blocker` afterwards moved the
 * severity chip and nothing else — and since the pipeline takes its next bug by
 * PRIORITY, the promoted report never moved up the queue. The only way to make a
 * blocker actually go first was to re-file it as a new report, which is what the
 * operator ended up doing.
 *
 * The rule, in one place so every writer shares it:
 *
 *  - A patch that SETS `priority` outright wins. The caller said so; deriving
 *    over the top would make the priority picker on three surfaces a lie.
 *  - A patch that changes neither `severity` nor `blockedWork` changes nothing.
 *  - A priority that was HAND-SET (it does not match what the CURRENT severity +
 *    blockedWork would derive) is a deliberate operator override and is KEPT.
 *    That is the same "overridden — filed severity suggests X" state the detail
 *    page already renders, so the two surfaces agree about what an override is.
 *  - Otherwise the priority follows the new severity.
 *
 * Returns the priority to write, or `null` when nothing should change (so a
 * caller can patch only when there is something to patch).
 */
export function derivePriorityOnTriageChange(
  current: Pick<BugReport, 'severity' | 'blockedWork' | 'priority'>,
  patch: { severity?: BugSeverity; blockedWork?: boolean; priority?: BugPriority },
): BugPriority | null {
  // The caller named a priority — that is the decision, not an input to one.
  if (patch.priority !== undefined) return null

  const nextSeverity = patch.severity ?? current.severity
  const nextBlocked = patch.blockedWork ?? current.blockedWork
  // Nothing the derivation reads has moved.
  if (nextSeverity === current.severity && nextBlocked === current.blockedWork) return null

  // A hand-set priority stays hand-set: triage is a judgement, and re-deriving
  // over it would silently discard the operator's call the next time anyone
  // touched severity.
  if (current.priority !== computePriority(current.severity, current.blockedWork)) return null

  const next = computePriority(nextSeverity, nextBlocked)
  return next === current.priority ? null : next
}

// ── Approval bookkeeping ─────────────────────────────────────────────────────

/** Who is changing the approval, and when. Supplied by the server, never the client. */
export interface ApprovalStampContext {
  /** Session email of the operator making the change. */
  actorEmail: string | null
  /** Which door the decision came through. */
  source: 'ui' | 'email_link'
  /** ISO 8601 timestamp to stamp an approval with. */
  now: string
}

/** The four approval fields, always written together. */
export interface ApprovalStamp {
  approvalStatus: BugApprovalStatus
  approvedForFixAt: string | null
  approvedBy: string | null
  approvalSource: 'email_link' | 'ui' | 'auto_blocker' | null
}

/**
 * The ONE definition of an approval transition.
 *
 * `approvedForFixAt` / `approvedBy` / `approvalSource` are not independent
 * fields — they are the receipt for ONE decision, and they only mean anything
 * while the bug is approved. Any transition that writes `approvalStatus`
 * without rewriting all three leaves a lie on the row.
 *
 * That is exactly what happened. Approval could be set from four places (the
 * email link, the detail page, the pipeline board, and now the roster) and
 * each derived the receipt differently in its own client: `approvedBy: 'UI'`
 * on the detail page, no `approvedBy` at all on the pipeline board, and the
 * revoke path wrote `approvalStatus: 'rejected'` alone — so a REJECTED bug
 * kept rendering "approved on Jul 12 by UI" underneath the red chip. Deriving
 * it here, from the server session, is what makes a fourth surface safe to add
 * rather than a fourth spelling of the same bug.
 *
 * Pure: takes the clock and the actor as input so it is unit-testable and so
 * the client can never be the one to decide who approved something.
 *
 * Rule — `approved` writes the receipt; `pending` and `rejected` CLEAR it,
 * because neither is an approval and a stale receipt is worse than none.
 */
export function buildApprovalStamp(
  next: BugApprovalStatus,
  ctx: ApprovalStampContext,
): ApprovalStamp {
  if (next !== 'approved') {
    return {
      approvalStatus: next,
      approvedForFixAt: null,
      approvedBy: null,
      approvalSource: null,
    }
  }
  return {
    approvalStatus: 'approved',
    approvedForFixAt: ctx.now,
    // Falls back to the source rather than null: "approved via ui" is still
    // more than the old literal 'UI', and an unauthenticated write cannot
    // reach here anyway (PATCH is behind enforceApiWrite).
    approvedBy: ctx.actorEmail ?? ctx.source,
    approvalSource: ctx.source,
  }
}

// ── ID generation ────────────────────────────────────────────────────────────

/**
 * Generate a short, human-friendly bug ID.
 * Format: BUG-<timestamp-base36>-<random4>
 * Example: BUG-lz4f8k-a3x9
 */
export function generateBugId(): string {
  const ts = Date.now().toString(36)
  const rand = Math.random().toString(36).substring(2, 6)
  return `BUG-${ts}-${rand}`
}

// ── Validation ───────────────────────────────────────────────────────────────

export interface ValidationError {
  field: string
  message: string
}

/**
 * Validate a bug report submission. Returns an array of errors (empty = valid).
 * Ensures the report is detailed enough to be actionable.
 */
export function validateBugReport(data: Partial<BugReport>): ValidationError[] {
  const errors: ValidationError[] = []

  if (!data.title || data.title.trim().length < 5) {
    errors.push({ field: 'title', message: 'Title must be at least 5 characters' })
  }
  if (!data.description || data.description.trim().length < 20) {
    errors.push({ field: 'description', message: 'Description must be at least 20 characters — explain what happened' })
  }
  if (!data.category || !BUG_CATEGORIES.includes(data.category as BugCategory)) {
    errors.push({ field: 'category', message: 'Select a category' })
  }
  if (!data.severity || !BUG_SEVERITIES.includes(data.severity as BugSeverity)) {
    errors.push({ field: 'severity', message: 'Select a severity level' })
  }
  if (!data.affectedArea || data.affectedArea.trim().length === 0) {
    errors.push({ field: 'affectedArea', message: 'Select or enter an affected area' })
  }
  if (!data.stepsToReproduce || data.stepsToReproduce.trim().length < 10) {
    errors.push({ field: 'stepsToReproduce', message: 'Steps to reproduce must be at least 10 characters' })
  }
  errors.push(...validateScreenshotCount(data as { screenshotUrls?: unknown }))
  errors.push(...validateAttachments(data as { attachments?: unknown; screenshotUrls?: unknown }))

  return errors
}

/**
 * Reject attachments the form should already have stopped: wrong type, too big,
 * too many, or a submission whose total payload cannot survive the request.
 *
 * The API is reachable directly, so every rule the picker enforces is enforced
 * again here — but the REASON this returns messages rather than silently
 * dropping is the bug it fixes. Jordan picked an .xlsx and the client discarded
 * it without a word; a rejection nobody can see is indistinguishable from a
 * broken feature.
 */
export function validateAttachments(
  data: { attachments?: unknown; screenshotUrls?: unknown },
): ValidationError[] {
  const errors: ValidationError[] = []
  const list = Array.isArray(data.attachments) ? data.attachments : []
  if (list.length > MAX_BUG_ATTACHMENTS) {
    errors.push({
      field: 'attachments',
      message: `Attach at most ${MAX_BUG_ATTACHMENTS} files (you attached ${list.length})`,
    })
  }
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue
    const rec = raw as Record<string, unknown>
    const name = typeof rec.name === 'string' ? rec.name : ''
    const dataUrl = typeof rec.dataUrl === 'string' ? rec.dataUrl : ''
    if (!isAllowedAttachmentName(name)) {
      errors.push({
        field: 'attachments',
        message: `"${name || 'file'}" is not a supported type — attach ${BUG_ATTACHMENT_EXTENSIONS.join(', ')} or an image`,
      })
      continue
    }
    const bytes = dataUrlBytes(dataUrl)
    if (bytes > MAX_ATTACHMENT_BYTES) {
      errors.push({
        field: 'attachments',
        message: `"${name}" is ${mbLabel(bytes)} — the limit is ${mbLabel(MAX_ATTACHMENT_BYTES)} per file`,
      })
    }
  }
  // Screenshots ride in the SAME request body, so they count toward the ceiling —
  // measured by the same helper the form's pre-flight uses, so the two doors can
  // never disagree about how big a submission is.
  const total = bugUploadBytes(data)
  if (total > MAX_BUG_UPLOAD_BYTES) {
    errors.push({ field: 'attachments', message: bugUploadTooLargeMessage(total) })
  }
  return errors
}

/** '2.4 MB' — shared by every size message so they read the same. */
export function mbLabel(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Reject an over-long screenshot list. Shared by the create and reporter-update
 * paths so the cap has one definition and both doors enforce it — the form
 * already stops at the cap, but the API is reachable directly.
 *
 * Counts the RAW submitted list, before `splitScreenshots` de-dupes/truncates:
 * silently discarding attachments the reporter believes they attached is worse
 * than an explicit error.
 */
export function validateScreenshotCount(
  data: { screenshotUrls?: unknown },
): ValidationError[] {
  if (!Array.isArray(data.screenshotUrls)) return []
  const supplied = data.screenshotUrls.filter(u => typeof u === 'string' && u.length > 0)
  if (supplied.length <= MAX_BUG_SCREENSHOTS) return []
  return [{
    field: 'screenshotUrls',
    message: `Attach at most ${MAX_BUG_SCREENSHOTS} screenshots (you attached ${supplied.length})`,
  }]
}

/**
 * Validate a PARTIAL update (the PATCH path). Unlike `validateBugReport` this
 * requires nothing — it only checks that any enum field PRESENT in the patch
 * carries a canonical value, comparing against the `BUG_*` constant arrays so
 * there is exactly one vocabulary.
 *
 * Why this exists (July 31 2026): `PATCH /api/bug-reports?id=X` piped
 * `await req.json()` straight into `updateBugReport`. The `Partial<Pick<…>>`
 * parameter type is COMPILE-TIME only, so any string reached KV. A caller
 * wrote `status: 'in-progress'` — the HYPHEN spelling this repo uses for
 * batches, maintenance and training statuses, but not for bug reports, whose
 * canonical value is `in_progress`. That one character took the entire
 * /settings/bugs roster down: the status→colour map had no `in-progress` key,
 * so the chip rendered `undefined.bg` and the client component threw, hiding
 * all 116 bugs behind "This page couldn't load".
 *
 * POST already validated; PATCH was the unguarded door. Both now agree.
 */
export function validateBugReportUpdate(updates: Record<string, unknown>): ValidationError[] {
  const errors: ValidationError[] = []
  const check = (field: string, allowed: readonly string[]) => {
    const v = updates[field]
    if (v === undefined) return               // absent = not being changed
    if (typeof v !== 'string' || !allowed.includes(v)) {
      errors.push({
        field,
        message: `Invalid ${field} "${String(v)}" — must be one of: ${allowed.join(', ')}`,
      })
    }
  }
  check('status', BUG_STATUSES)
  check('severity', BUG_SEVERITIES)
  check('priority', BUG_PRIORITIES)
  check('approvalStatus', BUG_APPROVAL_STATUSES)
  check('category', BUG_CATEGORIES)
  // `project` is retaggable (a bug filed from the wrong surface can be moved),
  // so it goes through the same vocabulary gate as every other enum. Without
  // it, a typo'd project would silently land in neither filter bucket.
  check('project', BUG_PROJECTS)
  return errors
}

// ── KV CRUD ──────────────────────────────────────────────────────────────────

/**
 * Compact bug-list shape returned by list endpoints.
 *
 * Operators paste full-resolution PNG screenshots into the bug form; those
 * land as base64 data URIs in `screenshotUrl` and average ~100 KB per row.
 * Before this stripping pass, GET /api/bug-reports returned 4.4 MB for 44
 * rows and TTFB was 1.5 s. The table never displays the screenshot inline
 * anyway — operators click into a single bug to see it — so list endpoints
 * return the row with `screenshotUrl: null` and a boolean `hasScreenshot`
 * the UI can use to render a 📷 indicator. Single-bug fetches via
 * `getBugReportById` still return the full screenshot.
 *
 * (Fabian asked May 2026: "why does the bug DB take so long to load?")
 */
export interface BugReportListItem extends Omit<BugReport, 'screenshotUrl' | 'additionalScreenshotUrls' | 'attachments'> {
  /** Always null in list responses — fetch ?id=X to get the data URI. */
  screenshotUrl: null
  /** Always empty in list responses — fetch ?id=X to get the data URIs. */
  additionalScreenshotUrls: []
  /** Always empty in list responses — fetch ?id=X to get the payloads. */
  attachments: []
  /** True when the source row carried at least one screenshot. */
  hasScreenshot: boolean
  /** How many screenshots the source row carried (0 when none). */
  screenshotCount: number
  /** How many non-image files the source row carried (0 when none). */
  attachmentCount: number
  /** Always null in list responses — the fix notes are long and only the detail page reads them. */
  pipelineFixNotes?: null
}

/**
 * Strip EVERY screenshot payload from a stored row, replacing them with a
 * `hasScreenshot` boolean and a `screenshotCount`. Pure helper kept in lib so
 * the list-shape contract has exactly one definition.
 *
 * EVERY payload field must be blanked. Stripping only `screenshotUrl` and
 * letting `additionalScreenshotUrls` through would reintroduce the very
 * regression this function exists to prevent — and worse, since the array can
 * hold three images where the legacy field held one. `attachments` is the third
 * such field and the heaviest of them: a spreadsheet can be megabytes, and the
 * index is itself persisted, so letting one through would put it in EVERY list
 * read for the life of the row.
 */
export function toBugListItem(row: BugReport): BugReportListItem {
  const shots = bugScreenshots(row)
  const files = bugAttachments(row)
  return {
    ...row,
    // Normalize here too, not only in the read paths: the index is itself
    // persisted, so a legacy row promoted into it must carry the canonical
    // project rather than re-deriving it on every subsequent list read.
    project: normalizeBugProject(row.project),
    screenshotUrl: null,
    additionalScreenshotUrls: [],
    attachments: [],
    hasScreenshot: shots.length > 0,
    screenshotCount: shots.length,
    attachmentCount: files.length,
    // The fix notes are a commit message per fix (kilobytes each, hundreds of rows): the detail
    // page is their only reader, and this index is fetched by every list, the board, the notify
    // cron and the tick's prefilter.
    pipelineFixNotes: null,
  }
}

/**
 * Lazy one-time migration from the legacy single-blob layout to the new
 * per-row + index layout. Safe to call before every operation — the cost
 * is a single existence check on the index key when already migrated.
 *
 * If the index is missing, reads the legacy blob, fans it out into one
 * row key per bug, then writes the index. Idempotent: re-running rewrites
 * the same data. The legacy key is NOT deleted (rollback safety).
 */
async function ensureMigrated(): Promise<void> {
  const index = await kvGet<BugReportListItem[]>(INDEX_KEY)
  if (index !== null) return // Already migrated
  const legacy = await kvGet<BugReport[]>(LEGACY_KEY) ?? []
  // Write rows first so a partial migration leaves the system in a
  // state where the (eventually written) index correctly points to
  // existing rows.
  await Promise.all(legacy.map(row => kvSet(rowKey(row.id), row)))
  await kvSet(INDEX_KEY, legacy.map(toBugListItem))
}

/**
 * Get all bug reports, optionally filtered by approval status and/or project.
 *
 * Every returned row carries a canonical `project` — rows stored before the
 * field existed are backfilled to `portal` on the way out (`withBugProject`),
 * which is what lets the roster's project buttons work without a KV migration.
 */
export async function getBugReports(
  approvalFilter?: BugApprovalStatus,
  projectFilter?: BugProject,
): Promise<BugReportListItem[]> {
  await ensureMigrated()
  const index = await kvGet<BugReportListItem[]>(INDEX_KEY) ?? []
  let results = index.map(withBugProject)
  if (approvalFilter) {
    results = results.filter(r => r.approvalStatus === approvalFilter)
  }
  if (projectFilter) {
    results = results.filter(r => r.project === projectFilter)
  }
  return results.sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

/**
 * Get approved bugs ready for implementation, sorted by priority then severity then date.
 * Excludes bugs already in 'fixed', 'verified', or 'closed' status.
 *
 * Returns the compact `BugReportListItem` shape (screenshotUrl stripped) —
 * the approved queue UI doesn't render thumbnails either.
 */
export async function getApprovedBugsForFixing(projectFilter?: BugProject): Promise<BugReportListItem[]> {
  const all = await getBugReports('approved', projectFilter)
  const excludeStatuses: BugStatus[] = ['fixed', 'verified', 'closed']
  const prioOrder: Record<BugPriority, number> = { critical: 0, high: 1, medium: 2, low: 3 }
  const sevOrder: Record<BugSeverity, number> = { blocker: 0, high: 1, medium: 2, low: 3 }
  return all
    .filter(b => !excludeStatuses.includes(b.status))
    .sort((a, b) => {
      const pd = (prioOrder[a.priority] ?? 9) - (prioOrder[b.priority] ?? 9)
      if (pd !== 0) return pd
      const sd = (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9)
      if (sd !== 0) return sd
      return new Date(a.approvedForFixAt || a.createdAt).getTime() - new Date(b.approvedForFixAt || b.createdAt).getTime()
    })
}

/**
 * Get a single bug report by ID, or null.
 *
 * Returns the FULL row including the `screenshotUrl` data URI — this is the
 * detail-page endpoint, the only one the operator hits when they need to
 * see the screenshot. Reads exactly one small KV key (the per-bug row),
 * not the whole DB.
 */
export async function getBugReportById(id: string): Promise<BugReport | null> {
  await ensureMigrated()
  const row = await kvGet<BugReport>(rowKey(id))
  // Backfill the project on the way out so the detail page, the resolve email
  // and the reporter self-service page never see the field absent.
  return row ? withBugProject(row) : null
}

/** Create a new bug report. Returns the created record. */
export async function createBugReport(
  data: Omit<BugReport, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'priority' | 'assignee' | 'resolutionSummary' | 'closedAt' | 'approvalStatus' | 'approvedForFixAt' | 'approvedBy' | 'approvalSource' | 'approvalNote' | 'project'>
    // `project` is OPTIONAL on the wire (an older client, or a surface that
    // simply doesn't set it) and is normalized to Portal below.
    & { project?: BugProject },
): Promise<BugReport> {
  await ensureMigrated()
  const now = new Date().toISOString()
  // `screenshotUrls` is a WIRE-ONLY field — the flat list the form posts. It is
  // normalized into the stored primary/extras pair below, so drop it here or
  // the spread would persist a third copy of every image on the row.
  const { screenshotUrls: _wireOnly, ...rest } = data as typeof data & { screenshotUrls?: unknown }
  const report: BugReport = {
    ...rest,
    // Normalize whatever shape the caller used (new array / legacy single) so
    // a row can never be stored with a missing or malformed extras array.
    ...normalizeScreenshotInput(data as { screenshotUrls?: unknown; screenshotUrl?: unknown }),
    // Same treatment for the non-image files: normalized, never spread raw, so a
    // malformed entry can't reach a render surface that expects a dataUrl.
    attachments: normalizeAttachmentInput(data as { attachments?: unknown }),
    // Stamped, never trusted raw: an unknown/absent project from the wire
    // lands in Portal rather than creating an invisible fourth bucket that no
    // roster filter would ever show.
    project: normalizeBugProject((data as { project?: unknown }).project),
    id: generateBugId(),
    createdAt: now,
    updatedAt: now,
    status: 'new',
    priority: computePriority(data.severity, data.blockedWork),
    assignee: null,
    resolutionSummary: null,
    closedAt: null,
    approvalStatus: 'pending',
    approvedForFixAt: null,
    approvedBy: null,
    approvalSource: null,
    approvalNote: null,
  }

  // Write the full row first, then append to the (compact) index. If the
  // index write fails mid-flight, the row exists but isn't yet listed —
  // a re-run of `getBugReports` won't show it, but a follow-up
  // `ensureMigrated` won't repair it either; this is acceptable for a
  // low-volume bug DB where the operator can retry. The reverse order
  // (index first, row second) would risk the list pointing at a
  // missing row, which is the worse failure mode.
  await kvSet(rowKey(report.id), report)
  // Append under compare-and-set: two reports filed in the same second would otherwise
  // read the same index and the second write would drop the first from the list.
  const listItem = toBugListItem(report)
  await kvMutate<BugReportListItem[]>(INDEX_KEY, (current) => [...(current ?? []), listItem])
  return report
}

/**
 * Fields a bug's original REPORTER may edit on their own report (via the
 * reporter-update flow — see /api/bug-reports/reporter-update). These are
 * the "what I experienced" content fields, NOT triage fields (severity/
 * priority/status remain admin-only). The screenshot fields let the reporter
 * add/replace an attachment when reactivating with new evidence.
 */
export type ReporterEditableField =
  | 'description'
  | 'expectedBehavior'
  | 'stepsToReproduce'
  | 'screenshotUrl'
  | 'additionalScreenshotUrls'

export interface ReporterEditInput {
  description?: string
  expectedBehavior?: string
  stepsToReproduce?: string
  /**
   * Non-image files to APPEND to those already on the bug, capped at
   * MAX_BUG_ATTACHMENTS in total. Append, never replace — a reporter adding the
   * spreadsheet they forgot must not wipe the one already there.
   */
  attachments?: unknown
  /**
   * LEGACY single attachment: a data URI REPLACES the primary screenshot;
   * null/absent leaves it alone. Kept so older clients behave as before.
   */
  screenshotUrl?: string | null
  /**
   * Data URIs to APPEND to the screenshots already on the bug, capped at
   * MAX_BUG_SCREENSHOTS in total.
   *
   * Append, not replace, on purpose: this endpoint's job is "I have more
   * evidence". A reporter can never blank an attachment they (or a colleague)
   * already filed — same guard the single-screenshot flow always had.
   */
  screenshotUrls?: string[] | null
  /** When true, a resolved/closed bug is pushed back to 'new' for triage. */
  reactivate?: boolean
  /**
   * When true, the REPORTER retracts their own still-open report: it closes
   * with a summary saying so and leaves every pipeline lane (a closed bug
   * stages as `done`). The opposite of `reactivate`, and gated by
   * `canReporterWithdraw` in the route — never assume it is allowed here.
   */
  withdraw?: boolean
  /** Who is withdrawing — named in the stored summary so the board reads as a fact. */
  withdrawnBy?: string | null
  /** Injected clock, for the withdrawal's closedAt. Defaults to now. */
  now?: Date
}

/**
 * The statuses a reporter may WITHDRAW their own report from (BUG-mvzi3cez-gfzk,
 * Jordan, Nov 2026 — "filed a bug, need to delete the bug").
 *
 * Open, not yet being built. A bug already `in_progress` is being worked RIGHT NOW
 * (or has a pushed fix waiting to verify), and a retraction landing mid-run would
 * strand a half-shipped change — that one goes through the operator. A
 * fixed/verified/closed bug has nothing to withdraw; its door is `reactivate`.
 */
export const REPORTER_WITHDRAWABLE_STATUSES = ['new', 'triaged', 'blocked'] as const

/**
 * May this reporter retract this report? Pure, so the route, the page and the
 * tests all read ONE rule — and it says WHY, because a refusal a reporter cannot
 * act on is the same as no answer.
 */
export function canReporterWithdraw(
  bug: Pick<BugReport, 'status'> & { pipelineFixPushedAt?: string | null },
): { ok: true } | { ok: false; reason: string } {
  if ((bug.pipelineFixPushedAt ?? '').trim()) {
    return {
      ok: false,
      reason: 'A fix for this report has already been pushed, so it can no longer be withdrawn. '
        + 'Wait for the resolution email — it has a Close button.',
    }
  }
  if (bug.status === 'fixed' || bug.status === 'verified' || bug.status === 'closed') {
    return { ok: false, reason: `This report is already ${bug.status} — there is nothing to withdraw.` }
  }
  if (!(REPORTER_WITHDRAWABLE_STATUSES as readonly string[]).includes(bug.status)) {
    return {
      ok: false,
      reason: 'This report is being worked on right now, so withdrawing it could leave a '
        + 'half-finished change. Ask Fabian to stop it instead.',
    }
  }
  return { ok: true }
}

/** The sentence a withdrawal stores as its resolution summary. One wording, one home. */
export function reporterWithdrawalSummary(args: { by?: string | null; now?: Date }): string {
  const who = (args.by ?? '').trim()
  const when = (args.now ?? new Date()).toISOString().slice(0, 10)
  return `Withdrawn by the reporter${who ? ` (${who})` : ''} on ${when}. `
    + 'Nothing was built — they asked for it to come off the work list.'
}

/**
 * Build the KV patch for a reporter edit. Pure + testable. Only copies
 * provided content fields (undefined fields are left untouched). When
 * `reactivate` is set, also flips status → 'new', clears the resolution
 * summary, and clears closedAt so the bug re-enters the active queue. When
 * `withdraw` is set, the opposite: status → 'closed' with a summary saying the
 * reporter retracted it (gate: `canReporterWithdraw`, checked by the route).
 *
 * `existing` is the row as currently stored, needed only to APPEND new
 * screenshots onto the ones already there. Omit it and the append is a no-op
 * (there is nothing to append to) — the legacy single-screenshot replace still
 * works either way, so older callers are unaffected.
 *
 * The returned object is exactly what updateBugReport() should be called
 * with — the route does NOT hand-roll status logic.
 */
export function buildReporterPatch(
  input: ReporterEditInput,
  existing?: ScreenshotBearing | null,
): Partial<Pick<BugReport,
  'description' | 'expectedBehavior' | 'stepsToReproduce' | 'screenshotUrl'
  | 'additionalScreenshotUrls' | 'attachments' | 'status' | 'resolutionSummary' | 'closedAt'>> {
  const patch: Partial<Pick<BugReport,
    'description' | 'expectedBehavior' | 'stepsToReproduce' | 'screenshotUrl'
    | 'additionalScreenshotUrls' | 'attachments' | 'status' | 'resolutionSummary' | 'closedAt'>> = {}
  if (typeof input.description === 'string') patch.description = input.description
  if (typeof input.expectedBehavior === 'string') patch.expectedBehavior = input.expectedBehavior
  if (typeof input.stepsToReproduce === 'string') patch.stepsToReproduce = input.stepsToReproduce

  // ── Screenshots ──────────────────────────────────────────────────────────
  // Two doors, and their order matters. The legacy single field REPLACES the
  // primary; the new array APPENDS. When a client sends both, the replacement
  // lands first and the appends stack on top of the replaced set — which is
  // what "here is a better main shot, plus these extras" should mean.
  const replacedPrimary =
    typeof input.screenshotUrl === 'string' && input.screenshotUrl.length > 0
      ? input.screenshotUrl
      : undefined
  if (replacedPrimary !== undefined) patch.screenshotUrl = replacedPrimary

  const additions = Array.isArray(input.screenshotUrls)
    ? input.screenshotUrls.filter(u => typeof u === 'string' && u.length > 0)
    : []
  if (additions.length > 0) {
    const base = bugScreenshots({
      screenshotUrl: replacedPrimary ?? existing?.screenshotUrl ?? null,
      additionalScreenshotUrls: existing?.additionalScreenshotUrls ?? [],
    })
    // splitScreenshots de-dupes and truncates, so re-sending an image the bug
    // already has is a no-op rather than a duplicate, and the cap holds even
    // if a caller ignores validateScreenshotCount.
    const merged = splitScreenshots([...base, ...additions])
    patch.screenshotUrl = merged.screenshotUrl
    patch.additionalScreenshotUrls = merged.additionalScreenshotUrls
  }

  // ── Non-image files ──────────────────────────────────────────────────────
  // APPEND-only, same as the screenshot extras: a reporter adding the
  // spreadsheet they forgot must not wipe the one already on the bug.
  // normalizeAttachmentInput de-dupes by payload and truncates to the cap, so
  // re-sending a file the bug already has is a no-op.
  const newFiles = normalizeAttachmentInput({ attachments: input.attachments })
  if (newFiles.length > 0) {
    patch.attachments = normalizeAttachmentInput({
      attachments: [...bugAttachments(existing as AttachmentBearing | null | undefined), ...newFiles],
    })
  }

  if (input.reactivate) {
    patch.status = 'new'
    patch.resolutionSummary = null
    patch.closedAt = null
  }
  // The mirror image: the reporter takes their own report OFF the list. Closing it
  // is all that is needed to stop the pipeline — `stageOf` reads any closed bug as
  // `done`, so it leaves every working lane without this patch touching one
  // pipeline field. The summary is what the board and their own page then show.
  if (input.withdraw) {
    const now = input.now ?? new Date()
    patch.status = 'closed'
    patch.closedAt = now.toISOString()
    patch.resolutionSummary = reporterWithdrawalSummary({ by: input.withdrawnBy, now })
  }
  return patch
}

/**
 * The fields any writer may patch onto a bug row. Named so the guarded writer below and
 * `updateBugReport` cannot drift apart — they are the same whitelist, and `bug-pipeline-bvt`
 * walks it against the pipeline's own field list.
 */
export type BugReportUpdate = Partial<Pick<BugReport,
    'status' | 'priority' | 'assignee' | 'resolutionSummary' | 'severity' | 'closedAt'
    | 'approvalStatus' | 'approvedForFixAt' | 'approvedBy' | 'approvalSource' | 'approvalNote'
    // Retag a bug into the other project (Aug 2026 — one DB, several apps):
    | 'project'
    // Reporter-editable content fields (reporter-update flow, May 2026):
    | 'description' | 'expectedBehavior' | 'stepsToReproduce' | 'screenshotUrl'
    | 'additionalScreenshotUrls'
    // `workaroundDetails` joins its three siblings above so the credential scrub can reach it
    // (BUG-muzhg0nt-n81q slice 3): it is prose a reporter types, so it can receive a pasted key,
    // and a field outside this whitelist would be planned, reported as scrubbed, and dropped.
    | 'workaroundDetails'
    // Fix-pipeline fields (docs/features/bug-fix-pipeline — written by the
    // tick via scripts/bug-pipeline/queue.mts and the notify cron; the field
    // list is walked against this whitelist by bug-pipeline-bvt):
    | 'fixRecommendation' | 'pipelinePlannedFiles' | 'fixRecommendationConfidence' | 'fixRecommendationVerdict' | 'fixRecommendationAt'
    | 'recommendationEmailedAt' | 'pipelineClaimedAt' | 'pipelineAttempts'
    | 'pipelineFixCommit' | 'pipelineFixPushedAt' | 'pipelineFixNotes' | 'resolveEmailPending'
    | 'resolveEmailedAt' | 'pipelineNotice' | 'pipelineNoticeEmailedAt'
    | 'pipelineNoticeAsksReporter' | 'pipelineQuestionEmailedAt'
    | 'pipelineBlockedReason' | 'pipelineOperatorGuidance' | 'pipelineOperatorGuidanceAt'
    | 'pipelineWorkLog' | 'pipelineQueueId'
    | 'consolidatedInto' | 'resolutionKind' | 'duplicateOf'>>

/** What a guarded write decided, having seen the CURRENT row. */
export type BugReportWritePlan = { patch: BugReportUpdate } | { refuse: string }

export type BugReportWriteResult =
  | { ok: true; row: BugReport }
  | { ok: false; reason: string }

/**
 * Update a bug row under COMPARE-AND-SET, deciding the patch from the row as it is at the
 * moment of writing.
 *
 * WHY (operator, Sep 10 2026 — "look at the serialization, that's capping throughput"). Every
 * bug write used to be `kvGet` → merge → `kvSet`, a read-then-write with no atomicity, and the
 * bug-fix pipeline's CLAIM is exactly that shape: read the row, check the stage is `fix`, write
 * the claim. Two ticks reading the same unclaimed bug would BOTH see `fix` and BOTH claim it —
 * two agents, one bug, two commits. That race is the only reason the tick workflow serializes
 * itself behind one global concurrency group, which caps the whole pipeline at one bug per run.
 *
 * The race was never hypothetical or pipeline-only: an operator approving on the board while the
 * notify cron writes the same row already loses one of the two writes.
 *
 * `plan` runs INSIDE the retry loop, against the freshly-read row, so a caller can both compute
 * from current values (attempt counts) and REFUSE when someone else got there first — which is
 * what makes a claim safe to run concurrently. Losing the compare-and-set is not an error: it
 * re-reads and asks `plan` again, so the loser sees the winner's row and can decline.
 */
export async function updateBugReportGuarded(
  id: string,
  plan: (current: BugReport) => BugReportWritePlan,
  opts: { dryRun?: boolean } = {},
): Promise<BugReportWriteResult> {
  await ensureMigrated()
  let refusal: string | null = null
  let written: BugReport | null = null
  try {
    await kvMutate<BugReport>(rowKey(id), (current) => {
      // Reset every attempt: the mutator re-runs after a lost race, and a decision carried over
      // from the previous read is a decision about a row that no longer exists.
      refusal = null
      written = null
      if (!current) { refusal = `bug ${id} not found`; return null }
      // Backfill before merging so a legacy row that is updated for any reason
      // (a status change, an approval) also acquires its canonical project.
      const existing = withBugProject(current)
      const decision = plan(existing)
      if ('refuse' in decision) { refusal = decision.refuse; return null }
      const now = new Date().toISOString()
      // Priority FOLLOWS severity, on every door. Derived here rather than in the
      // PATCH route because severity is editable from the roster, the detail page
      // and the API alike — a per-caller copy is how promoting a bug to `blocker`
      // came to move its chip and not its place in the queue (BUG-mucb7ttp-rujy).
      // Returns null unless there is genuinely something to change, so an ordinary
      // patch writes exactly what it wrote before.
      const derivedPriority = derivePriorityOnTriageChange(existing, decision.patch)
      written = {
        ...existing,
        ...decision.patch,
        ...(derivedPriority ? { priority: derivedPriority } : {}),
        updatedAt: now,
        closedAt: decision.patch.status === 'closed'
          ? (decision.patch.closedAt ?? now)
          : existing.closedAt,
      }
      // DRY RUN: `written` is the row this call WOULD store, computed by the real
      // merge above. Returning null leaves KV untouched, so the caller can ask
      // "what would this patch do?" without doing it. Read-only by construction —
      // the only door the cloud tick has for a server-side write rule it cannot
      // otherwise observe (BUG-mucb7ttp-rujy).
      if (opts.dryRun) return null
      return written
    })
  } catch (err) {
    // Contention exhausted the retry budget. A refusal, never a throw: every caller of this is a
    // writer that can simply come back on the next pass, and a thrown error inside a tick would
    // read as a failed run.
    return { ok: false, reason: `could not write ${id}: ${String(err).slice(0, 140)}` }
  }
  if (refusal !== null || !written) return { ok: false, reason: refusal ?? `bug ${id} not found` }
  const row: BugReport = written
  // A dry run stops here: nothing was stored, so there is nothing to mirror.
  if (opts.dryRun) return { ok: true, row }
  // Mirror the change into the index entry so list endpoints reflect it immediately — under
  // compare-and-set too. The index is ONE key holding EVERY row's summary, so the plain
  // read-modify-write it used to do meant two updates to DIFFERENT bugs clobbered each other:
  // the losing bug's entry silently reverted, and a stale index entry is precisely what makes
  // the pipeline hand out a bug that is already claimed.
  const listItem = toBugListItem(row)
  await kvMutate<BugReportListItem[]>(INDEX_KEY, (current) => {
    const index = current ?? []
    const idx = index.findIndex(r => r.id === id)
    // If the row isn't in the index (drift safety), we append.
    if (idx === -1) return [...index, listItem]
    const next = index.slice()
    next[idx] = listItem
    return next
  })
  return { ok: true, row }
}

/** Update an existing bug report (partial update). Returns the updated record or null. */
export async function updateBugReport(
  id: string,
  updates: BugReportUpdate,
): Promise<BugReport | null> {
  const res = await updateBugReportGuarded(id, () => ({ patch: updates }))
  return res.ok ? res.row : null
}

/** What `attachBugFixNotes` did with one row. */
export type AttachFixNotesOutcome = 'written' | 'kept' | 'missing' | 'empty'

/**
 * Attach fix notes to a row and change NOTHING else — the backfill's writer.
 *
 * A live fix records its notes through the ordinary guarded writer (`fix-pushed`), which is right
 * for an event that is happening now. Backfilling hundreds of bugs fixed months ago is a different
 * act: routing it through `updateBugReportGuarded` would stamp every one of them "updated today"
 * and rewrite the megabyte index once per bug. So this writes the ROW only — no `updatedAt`, no
 * index mirror (the index never carries the notes; `toBugListItem` blanks them) — and by default
 * refuses to replace notes that are already there, so re-running it is a no-op.
 */
export async function attachBugFixNotes(
  id: string,
  notes: string,
  opts: { overwrite?: boolean; dryRun?: boolean } = {},
): Promise<AttachFixNotesOutcome> {
  const text = (notes ?? '').trim()
  if (!text) return 'empty'
  await ensureMigrated()
  let outcome: AttachFixNotesOutcome = 'missing'
  await kvMutate<BugReport>(rowKey(id), (current) => {
    // Reset every attempt — the mutator re-runs after a lost race (see updateBugReportGuarded).
    outcome = 'missing'
    if (!current) return null
    const existing = typeof current.pipelineFixNotes === 'string' ? current.pipelineFixNotes.trim() : ''
    if (existing && !opts.overwrite) { outcome = 'kept'; return null }
    if (existing === text) { outcome = 'kept'; return null }
    outcome = 'written'
    return opts.dryRun ? null : { ...current, pipelineFixNotes: text }
  })
  return outcome
}

/**
 * Hard-delete a bug report from KV storage.
 *
 * There is no "trash" — the record is removed entirely. Callers should
 * confirm with the user before invoking. Returns true if a record was
 * removed, false if the id didn't match anything.
 *
 * Two writes: delete the per-bug row + rewrite the index without it.
 */
export async function deleteBugReport(id: string): Promise<boolean> {
  await ensureMigrated()
  let removedFromIndex = false
  // Try the row delete regardless of index state — defensive.
  const removedRow = (await kvDel(rowKey(id))) > 0
  await kvMutate<BugReportListItem[]>(INDEX_KEY, (current) => {
    const index = current ?? []
    const kept = index.filter(r => r.id !== id)
    removedFromIndex = kept.length !== index.length
    return removedFromIndex ? kept : null
  })
  return removedRow || removedFromIndex
}

/**
 * Hard-delete multiple bug reports. Returns the number of records actually
 * removed (ids not found are silently skipped). Writes the index once
 * regardless of batch size; per-row deletes happen in parallel.
 */
export async function deleteBugReports(ids: string[]): Promise<number> {
  if (!Array.isArray(ids) || ids.length === 0) return 0
  await ensureMigrated()
  const toRemove = new Set(ids)
  let removed = 0
  // Delete the rows in parallel (Upstash REST has no MULTI but it's
  // happy with concurrent requests on a low-volume bug DB).
  await Promise.all(ids.map(id => kvDel(rowKey(id))))
  await kvMutate<BugReportListItem[]>(INDEX_KEY, (current) => {
    const index = current ?? []
    const kept = index.filter(r => !toRemove.has(r.id))
    removed = index.length - kept.length
    return removed > 0 ? kept : null
  })
  return removed
}
